<script setup lang="ts">
  import { IconDeactivate, IconLoading } from '@gat-ship/icons'
  import { testIds, type Company } from '@gat-ship/types'
  import { computed, ref } from 'vue'
  import { useCompanyCollectionContext } from '../../composables/useCompanyCollection'
  import {
    Modal,
    ModalHeader,
    ModalHeaderTitle,
    ModalSection,
    ModalContent,
    ModalFooter,
    Button
  } from '@gat-ship/vue-components'
  const companyProvider = useCompanyCollectionContext()!

  /** The open-state of the dialog. */
  const isOpen = ref(false)

  const isLoading = ref(false)

  const company = ref<Company | undefined>()

  const companyName = computed(() => company.value?.name)

  function closeDialog() {
    company.value = undefined
    isLoading.value = false
    isOpen.value = false
  }

  export interface DeactivateCompanyModalParams {
    company: Company
  }

  async function onDeactivateCompanyClick() {
    if (!company.value) {
      throw new Error('No company found')
    }
    isLoading.value = true
    try {
      await companyProvider.commands.deactivate(company.value.code)
    } finally {
      closeDialog()
    }
  }

  function showModal(params: DeactivateCompanyModalParams) {
    isOpen.value = true
    company.value = params.company
  }

  defineExpose({
    showModal
  })
</script>
<template>
  <Modal v-model:open="isOpen" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>
        <IconDeactivate />
        Deactivate Company
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <div class="text-sm">
          You are about to start the process of deactivating
          <span class="font-semibold">{{ companyName ?? 'X' }}</span
          >.
        </div>
        <div class="text-lg font-semibold">What else you should know</div>
        <div class="font-regular text-sm">
          You can restore your company if it was accidentally or wrongfully
          deactivated for up to
          <span class="underline">30 days</span> after deactivation.
        </div>
        <div class="font-regular text-sm">
          This company will still be visible in lists and other places where it
          has previously been used, but can not be assigned or used in the
          future after deactivation.
        </div>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <div v-if="isLoading" class="text-muted-foreground text-xs">
        Deactivating company...
      </div>
      <Button
        :data-testid="testIds.common.dialog.saveButton"
        :disabled="isLoading"
        @click="onDeactivateCompanyClick"
      >
        <IconLoading v-if="isLoading" class="size-4" />
        Deactivate Company
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
